import { useCallback, useEffect, useRef, useState } from 'react'
import type { LiveAccess } from './monitoringApi'
import './liveRecovery.css'
import { CustomLivePlayer } from './CustomLivePlayer'
import { preferredLiveSource, rememberLiveSource } from './liveSourcePreference'
import { useAuth } from '../auth/AuthContext'

type Props = {
  name: string
  companyId?: string
  cameraId?: string
  getAccess: (signal?: AbortSignal) => Promise<LiveAccess>
  active?: boolean
  onDimensions?: (width: number, height: number) => void
  onFrameCaptureReady?: (capture: () => Promise<Blob>) => void
}

type PlayerState = 'connecting' | 'playing' | 'waiting' | 'offline' | 'stopped'
type Source = 'preview' | 'whep'
type MediaProtocol = 'udp' | 'tcp'
type Sample = {
  at: number; frames: number; keyFrames: number; bytes: number; lost: number; received: number;
  decode: number; buffer: number; emitted: number; freezes: number;
  jitter: number | null; rtt: number | null; freezeDuration: number | null;
  framesDropped: number | null; packetsDiscarded: number | null;
  localProtocol: MediaProtocol | null; remoteProtocol: MediaProtocol | null;
}
type Metrics = {
  source: Source;
  protocol: MediaProtocol | null; localProtocol: MediaProtocol | null; remoteProtocol: MediaProtocol | null;
  fps: number; kbps: number; lossPercent: number; decodeMs: number | null; bufferMs: number | null;
  freezes: number; deltaFreezes: number; frozenMs: number | null; jitterMs: number | null;
  rttMs: number | null; framesDropped: number | null; packetsDiscarded: number | null; sampleMs: number;
  frozenMsTotal: number | null; framesDroppedTotal: number | null; packetsDiscardedTotal: number | null;
}
type DiagnosticEntry = {
  atUtc: string; attempt: number; source: Source; phase: string; peer: string; ice: string;
  attemptMs: number; firstFrameMs: number | null; bytesReceived: number; framesDecoded: number;
  keyFramesDecoded: number; packetAgeMs: number | null; decodedAgeMs: number | null;
  presentedAgeMs: number | null; metrics: Metrics | null;
}
type Attempt = {
  abort: AbortController
  peer: RTCPeerConnection | null
  stream: MediaStream | null
  startedAt: number
  answeredAt: number
  firstFrameAt: number
  playbackSince: number
  lastPlaybackAt: number
  sameSourceRetryReady: boolean
  decodedAt: number
  presentedAt: number
  disconnectedAt: number
  playbackRetriedAt: number
  packetAt: number
  number: number
  source: Source
  phase: string
  metrics?: Metrics
  sample?: Sample
  sampling: boolean
  statsUnavailable?: boolean
  alternate?: Source
  codec?: string
  compatibility?: boolean
  preferenceSaved?: boolean
}

const retryDelays = [200, 700, 1500, 3000, 5000, 8000]
const stalledFrameMs = 4500
const firstFrameMs = 8000
const keyFrameWaitMs = 12000
const packetFreshMs = 3000
const disconnectedGraceMs = 1200
const stablePlaybackMs = 10000
const sameSourceRetryPlaybackMs = 30000

// Bound response bodies and browser promises as well as HTTP negotiation.
// Disposed attempts must never attach tracks or start a late WHEP request.
function bounded<T>(promise: Promise<T>, milliseconds: number, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const finish = (callback: () => void) => {
      window.clearTimeout(timer)
      signal.removeEventListener('abort', abort)
      callback()
    }
    const abort = () => finish(() => reject(new DOMException('Cancelled', 'AbortError')))
    const timer = window.setTimeout(() => finish(() => reject(new Error('stage_timeout'))), milliseconds)
    signal.addEventListener('abort', abort, { once: true })
    promise.then(value => finish(() => resolve(value)), error => finish(() => reject(error)))
    if (signal.aborted) abort()
  })
}

export function WebRtcPlayer({ name, companyId, cameraId, getAccess, active = true, onDimensions, onFrameCaptureReady }: Props) {
  const { user } = useAuth()
  const preferenceScope = companyId && cameraId ? `${user?.login ?? ''}/${companyId}/${cameraId}` : ''
  const videoRef = useRef<HTMLVideoElement>(null)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const callbacks = useRef({ onDimensions, onFrameCaptureReady })
  callbacks.current = { onDimensions, onFrameCaptureReady }
  const [hasRenderedVideo, setHasRenderedVideo] = useState(false)
  const [fallbackFrame, setFallbackFrame] = useState('')
  const [state, setState] = useState<PlayerState>('connecting')
  const [audioChoice, setAudioChoice] = useState({ name, enabled: false })
  const audioEnabled = audioChoice.name === name && audioChoice.enabled
  const [audioControlVisible, setAudioControlVisible] = useState(false)
  const audioControlTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const showAudioControl = () => {
    setAudioControlVisible(true)
    clearTimeout(audioControlTimer.current)
    audioControlTimer.current = setTimeout(() => setAudioControlVisible(false), 3000)
  }
  useEffect(() => () => clearTimeout(audioControlTimer.current), [])
  const [diagnostic, setDiagnostic] = useState('none')
  const [diagnosticsEnabled] = useState(() => new URLSearchParams(window.location.search).get('liveDiagnostics') === '1')
  const [forcedSource] = useState<Source | undefined>(() => {
    const query = new URLSearchParams(window.location.search)
    const requested = query.get('liveSource')
    return query.get('liveDiagnostics') === '1' && (requested === 'preview' || requested === 'whep') ? requested : undefined
  })
  const [forceTcp] = useState(() => {
    const query = new URLSearchParams(window.location.search)
    return query.get('liveDiagnostics') === '1' && query.get('liveTransport') === 'tcp'
  })
  const [streamSource, setStreamSource] = useState<Source | ''>('')
  const diagnosticLog = useRef<DiagnosticEntry[]>([])
  const [diagnosticSnapshot, setDiagnosticSnapshot] = useState<DiagnosticEntry | null>(null)
  const cameraName = useRef(name)
  cameraName.current = name

  const downloadDiagnostics = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify({ schema: 1, camera: cameraName.current,
      capturedAtUtc: new Date().toISOString(), samples: diagnosticLog.current }, null, 2)], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'spectra-transmissao-diagnostico.json'
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const captureFrame = useCallback(() => new Promise<Blob>((resolve, reject) => {
    const video = videoRef.current
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !video.videoWidth || !video.videoHeight) {
      reject(new Error('video_frame_unavailable'))
      return
    }
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d', { alpha: false })
      if (!context) throw new Error('canvas_context_unavailable')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('video_frame_encoding_failed')), 'image/png')
    } catch (error) {
      reject(error)
    }
  }), [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let disposed = false
    let attempt: Attempt | null = null
    let retryTimer: number | undefined
    let frameCallback: number | undefined
    let retries = 0
    let rendered = false
    let playing = false
    let dimensions = ''
    let source: Source = forcedSource ?? 'preview'
    let preferenceLoaded = false
    let preferCompatibility = false
    let preferredCodec: string | undefined
    let inViewport = true
    let wasOnScreen = false
    let resumeUntil = 0
    let offline = !navigator.onLine
    let lastMediaTime = -1
    let attemptNumber = 0
    let lastDiagnosticAt = 0
    const supportsFrameCallback = typeof video.requestVideoFrameCallback === 'function'

    setHasRenderedVideo(false)
    setFallbackFrame('')
    diagnosticLog.current = []
    setDiagnosticSnapshot(null)
    setStreamSource('')
    setState(active ? 'connecting' : 'stopped')
    setDiagnostic(active ? 'access' : 'inactive')
    if (surfaceRef.current) delete surfaceRef.current.dataset.liveMetrics
    if (!active) return

    const current = (own: Attempt) => !disposed && attempt === own && !own.abort.signal.aborted
    function recordDiagnostic(own: Attempt, force = false) {
      if (!diagnosticsEnabled || disposed) return
      const now = performance.now()
      if (!force && now - lastDiagnosticAt < 5000) return
      lastDiagnosticAt = now
      const age = (at: number) => at ? Math.max(0, Math.round(now - at)) : null
      const entry: DiagnosticEntry = {
        atUtc: new Date().toISOString(), attempt: own.number, source: own.source, phase: own.phase,
        peer: own.peer?.connectionState ?? 'none', ice: own.peer?.iceConnectionState ?? 'none',
        attemptMs: Math.round(now - own.startedAt),
        firstFrameMs: own.firstFrameAt ? Math.round(own.firstFrameAt - own.startedAt) : null,
        bytesReceived: own.sample?.bytes ?? 0, framesDecoded: own.sample?.frames ?? 0,
        keyFramesDecoded: own.sample?.keyFrames ?? 0, packetAgeMs: age(own.packetAt),
        decodedAgeMs: age(own.decodedAt), presentedAgeMs: age(own.presentedAt), metrics: own.metrics ?? null,
      }
      // Only fixed phases and aggregate media counters; never URLs, SDP, tickets or candidate addresses.
      diagnosticLog.current = [...diagnosticLog.current.slice(-179), entry]
      setDiagnosticSnapshot(entry)
    }
    function phase(own: Attempt, value: string) {
      own.phase = value
      setDiagnostic(value)
      recordDiagnostic(own, true)
    }
    const surfaceVisible = () => {
      if (document.hidden || !inViewport) return false
      const surface = surfaceRef.current
      if (!surface) return false
      const rect = surface.getBoundingClientRect()
      const left = Math.max(0, rect.left), right = Math.min(window.innerWidth, rect.right)
      const top = Math.max(0, rect.top), bottom = Math.min(window.innerHeight, rect.bottom)
      if (left >= right || top >= bottom) return false
      // Intersection alone still considers cameras behind a focused tile or
      // dialog visible. Their decoder is the health signal, not the compositor.
      const covering = document.elementFromPoint((left + right) / 2, (top + bottom) / 2)
      return covering !== null && (surface.contains(covering) || covering.contains(surface))
    }
    const onScreen = () => {
      const visible = surfaceVisible()
      if (visible && !wasOnScreen) resumeUntil = performance.now() + 2000
      wasOnScreen = visible
      return visible
    }

    function preserveFrame() {
      if (!rendered || video!.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || !video!.videoWidth) return
      try {
        const canvas = document.createElement('canvas')
        canvas.width = Math.min(video!.videoWidth, Math.max(480, Math.ceil(video!.clientWidth * 2)))
        canvas.height = Math.max(1, Math.round(canvas.width * video!.videoHeight / video!.videoWidth))
        const context = canvas.getContext('2d', { alpha: false })
        if (!context) return
        context.drawImage(video!, 0, 0, canvas.width, canvas.height)
        setFallbackFrame(canvas.toDataURL('image/jpeg', 0.76))
      } catch { /* A snapshot is optional; recovery does not depend on it. */ }
    }

    function closeAttempt() {
      const previous = attempt
      attempt = null
      playing = false
      dimensions = ''
      if (frameCallback !== undefined) video!.cancelVideoFrameCallback(frameCallback)
      frameCallback = undefined
      if (!previous) return
      previous.abort.abort()
      if (previous.peer) {
        previous.peer.ontrack = null
        previous.peer.onconnectionstatechange = null
        previous.peer.oniceconnectionstatechange = null
        previous.peer.onicegatheringstatechange = null
      }
      for (const track of previous.stream?.getTracks() ?? []) {
        track.onended = null
        track.onmute = null
        track.onunmute = null
        track.stop()
      }
      previous.peer?.close()
      video!.srcObject = null
    }

    function reconnect(reason: string, immediate = false) {
      if (disposed || retryTimer !== undefined) return
      preserveFrame()
      if (attempt) phase(attempt, reason)
      if (!forcedSource && attempt?.alternate) {
        // Preserve a proven source through transient network failures.
        source = attempt.firstFrameAt ? attempt.source : attempt.alternate
      }
      closeAttempt()
      setState(offline ? 'offline' : 'waiting')
      setDiagnostic(reason)
      if (offline) return
      const base = immediate ? 150 : retryDelays[Math.min(retries++, retryDelays.length - 1)]
      const delay = Math.min(8000, Math.round(base * (0.85 + Math.random() * 0.3)))
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined
        void connect()
      }, delay)
    }

    function frameArrived(own: Attempt, presented: boolean) {
      if (!current(own) || !own.stream || video!.srcObject !== own.stream) return
      const now = performance.now()
      if (!own.playbackSince || now - own.lastPlaybackAt >= stalledFrameMs) own.playbackSince = now
      own.lastPlaybackAt = now
      if (now - own.playbackSince >= sameSourceRetryPlaybackMs) own.sameSourceRetryReady = true
      if (presented) own.presentedAt = now
      if (!own.firstFrameAt) {
        own.firstFrameAt = now
        if (surfaceRef.current) surfaceRef.current.dataset.firstFrameMs = String(Math.round(now - own.startedAt))
      }
      if (now - own.playbackSince >= stablePlaybackMs) {
        retries = 0
        if (!forcedSource && !own.preferenceSaved && own.codec) {
          own.preferenceSaved = true
          void rememberLiveSource(preferenceScope, own.compatibility ? 'compatibility' : own.source, own.codec)
            .catch(() => { if (current(own)) own.preferenceSaved = false })
        }
      }
      // 'Connected' alone cannot clear the recovery notice or reset backoff.
      if (!rendered) {
        rendered = true
        setHasRenderedVideo(true)
      }
      if (playing) return
      playing = true
      setState('playing')
      phase(own, 'frames')
      setFallbackFrame(previous => previous ? '' : previous)
    }

    function watchFrames(own: Attempt) {
      if (!supportsFrameCallback) return
      if (frameCallback !== undefined) video!.cancelVideoFrameCallback(frameCallback)
      const next = () => {
        frameCallback = video!.requestVideoFrameCallback(() => {
          if (!current(own)) return
          frameArrived(own, true)
          next()
        })
      }
      next()
    }

    function reportDimensions() {
      if (!attempt || !current(attempt) || video!.srcObject !== attempt.stream || !video!.videoWidth) return
      const next = video!.videoWidth + 'x' + video!.videoHeight
      if (dimensions === next) return
      dimensions = next
      callbacks.current.onDimensions?.(video!.videoWidth, video!.videoHeight)
      callbacks.current.onFrameCaptureReady?.(captureFrame)
    }

    async function connect() {
      if (disposed || attempt || offline) return
      const own: Attempt = {
        abort: new AbortController(), peer: null, stream: null, startedAt: performance.now(),
        answeredAt: 0, firstFrameAt: 0, playbackSince: 0, lastPlaybackAt: 0, sameSourceRetryReady: false,
        decodedAt: 0, presentedAt: 0,
        disconnectedAt: 0, playbackRetriedAt: 0, packetAt: 0, sampling: false,
        number: ++attemptNumber, source, phase: 'access',
      }
      attempt = own
      lastMediaTime = -1
      const signal = own.abort.signal
      setState(rendered ? 'waiting' : 'connecting')
      if (surfaceRef.current) {
        delete surfaceRef.current.dataset.liveMetrics
        delete surfaceRef.current.dataset.streamTransport
      }
      phase(own, 'access')
      try {
        if (!preferenceLoaded && !forcedSource) {
          const saved = await bounded(preferredLiveSource(preferenceScope, signal), 2000, signal).catch(() => undefined)
          if (!current(own)) return
          preferenceLoaded = true
          preferCompatibility = saved?.source === 'compatibility'
          preferredCodec = saved?.codec
          source = saved?.source === 'preview' ? 'preview' : saved ? 'whep' : source
        }
        const access = await bounded(getAccess(signal), 5000, signal)
        if (!current(own)) return
        const hasPreview = Boolean(access.previewUrl && access.previewAccessToken)
        if (forcedSource === 'preview' && !hasPreview) throw new Error('diagnostic_source_unavailable')
        const usePreview = source === 'preview' && hasPreview
        own.source = usePreview ? 'preview' : 'whep'
        setStreamSource(own.source)
        own.alternate = !forcedSource && hasPreview && access.previewUrl !== access.whepUrl ? (usePreview ? 'whep' : 'preview') : undefined
        const peer = new RTCPeerConnection()
        own.peer = peer
        const videoReceiver = peer.addTransceiver('video', { direction: 'recvonly' })
        const receiveCodecs = RTCRtpReceiver.getCapabilities('video')?.codecs ?? []
        // This player only receives: advertise decoder capabilities, including
        // HEVC on devices that can decode it but cannot encode it.
        if (receiveCodecs.length && typeof videoReceiver.setCodecPreferences === 'function') {
          const mime = `video/${preferredCodec === 'hevc' ? 'h265' : preferredCodec}`
          const ordered = [...receiveCodecs].sort((a, b) => Number(b.mimeType.toLowerCase() === mime) - Number(a.mimeType.toLowerCase() === mime))
          videoReceiver.setCodecPreferences(ordered)
        }
        if (surfaceRef.current) surfaceRef.current.dataset.receiveCodecs = [...new Set(receiveCodecs.map(codec => codec.mimeType))].join(',')
        // An unrequested audio transceiver would still consume network/decoder resources.
        if (audioEnabled) peer.addTransceiver('audio', { direction: 'recvonly' })
        peer.ontrack = event => {
          if (!current(own)) return
          if (event.track.kind === 'audio') {
            event.track.enabled = audioEnabled
            if (!audioEnabled) return
          }
          own.stream ??= new MediaStream()
          if (!own.stream.getTracks().includes(event.track)) own.stream.addTrack(event.track)
          video!.srcObject = own.stream
          if (event.track.kind === 'video') watchFrames(own)
          void video!.play().catch(() => undefined)
          event.track.onended = () => { if (current(own)) reconnect('track_ended') }
          event.track.onunmute = () => { if (current(own)) void video!.play().catch(() => undefined) }
        }
        const connectionChanged = () => {
          if (!current(own)) return
          if (peer.connectionState === 'failed' || peer.connectionState === 'closed' || peer.iceConnectionState === 'failed') {
            reconnect('transport_failed')
          } else if (peer.connectionState === 'disconnected' || peer.iceConnectionState === 'disconnected') {
            own.disconnectedAt ||= performance.now()
          } else if (peer.connectionState === 'connected') {
            own.disconnectedAt = 0
          }
        }
        peer.onconnectionstatechange = connectionChanged
        peer.oniceconnectionstatechange = connectionChanged
        phase(own, 'offer')
        const offer = await bounded(peer.createOffer(), 2000, signal)
        if (surfaceRef.current) surfaceRef.current.dataset.offerHevc = String(/a=rtpmap:\d+ H265\/90000/i.test(offer.sdp ?? ''))
        if (!current(own)) return
        await bounded(peer.setLocalDescription(offer), 2000, signal)
        if (!current(own)) return
        phase(own, 'ice')
        if (peer.iceGatheringState !== 'complete') {
          await new Promise<void>(resolve => {
            const finish = () => {
              window.clearTimeout(timer)
              signal.removeEventListener('abort', finish)
              peer.onicegatheringstatechange = null
              resolve()
            }
            const timer = window.setTimeout(finish, 3500)
            signal.addEventListener('abort', finish, { once: true })
            peer.onicegatheringstatechange = () => { if (peer.iceGatheringState === 'complete') finish() }
          })
        }
        if (!current(own)) return
        phase(own, 'whep')
        const initialUrl = new URL(usePreview ? access.previewUrl! : access.whepUrl, window.location.origin)
        const directCompatibility = preferCompatibility && !forcedSource && !usePreview &&
          /\/api\/live\/rtmp\/[0-9a-f-]{36}\/whep$/i.test(initialUrl.pathname)
        if (directCompatibility) initialUrl.searchParams.set('profile', 'medium')
        let response = await bounded(fetch(initialUrl.href, {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + (usePreview ? access.previewAccessToken : access.accessToken), 'Content-Type': 'application/sdp' },
          body: peer.localDescription?.sdp,
          cache: 'no-store',
          signal,
        }), directCompatibility ? 10000 : 5000, signal)
        if (!current(own)) return
        if (directCompatibility) {
          own.compatibility = response.ok
          if (!response.ok) preferCompatibility = false
        }
        if (!response.ok && (response.status === 400 || response.status === 503) &&
            !receiveCodecs.some(codec => codec.mimeType.toLowerCase() === 'video/h265')) {
          const compatible = new URL(access.whepUrl, window.location.origin)
          // Request the existing authenticated, shared H.264 relay only after
          // native negotiation fails. The source camera and recording stay intact.
          if (/\/api\/live\/rtmp\/[0-9a-f-]{36}\/whep$/i.test(compatible.pathname)) {
            compatible.searchParams.set('profile', 'medium')
            own.source = 'whep'
            setStreamSource('whep')
            phase(own, 'compatibility')
            response = await bounded(fetch(compatible.href, {
              method: 'POST',
              headers: { Authorization: 'Bearer ' + access.accessToken, 'Content-Type': 'application/sdp' },
              body: peer.localDescription?.sdp,
              cache: 'no-store', signal,
            }), 10000, signal)
            if (!current(own)) return
            if (response.ok) {
              own.compatibility = true
              preferCompatibility = true
              const lease = new URL(compatible.href)
              lease.pathname = lease.pathname.replace(/\/whep$/, '/compatibility-lease')
              lease.search = ''
              const leaseTimer = window.setInterval(() => {
                if (!current(own)) { window.clearInterval(leaseTimer); return }
                void bounded(fetch(lease.href, {
                  method: 'POST', headers: { Authorization: 'Bearer ' + access.accessToken },
                  cache: 'no-store', signal,
                }), 5000, signal).catch(() => {})
              }, 25000)
              signal.addEventListener('abort', () => window.clearInterval(leaseTimer), { once: true })
            }
          }
        }
        if (!response.ok) throw new Error('whep_' + response.status)
        if (directCompatibility) {
          const lease = new URL(initialUrl.href)
          lease.pathname = lease.pathname.replace(/\/whep$/, '/compatibility-lease')
          lease.search = ''
          const timer = window.setInterval(() => {
            if (!current(own)) { window.clearInterval(timer); return }
            void bounded(fetch(lease.href, { method: 'POST', headers: { Authorization: 'Bearer ' + access.accessToken },
              cache: 'no-store', signal }), 5000, signal).catch(() => {})
          }, 25000)
          signal.addEventListener('abort', () => window.clearInterval(timer), { once: true })
        }
        let answer = await bounded(response.text(), 3000, signal)
        if (!current(own)) return
        if (forceTcp) {
          // Diagnostic A/B only: use candidates already authorized by the server.
          // Never print SDP, credentials, candidate IDs or candidate addresses.
          const lines = answer.split(/\r?\n/)
          const candidateProtocol = (line: string) => /^a=candidate:/i.test(line) ? line.trim().split(/\s+/)[2]?.toLowerCase() : undefined
          if (!lines.some(line => candidateProtocol(line) === 'tcp')) throw new Error('diagnostic_tcp_unavailable')
          answer = lines.filter(line => candidateProtocol(line) !== 'udp').join('\r\n')
        }
        await bounded(peer.setRemoteDescription({ type: 'answer', sdp: answer }), 2000, signal)
        if (!current(own)) return
        own.answeredAt = performance.now()
        if (!own.firstFrameAt) phase(own, 'awaiting_frames')
      } catch (error) {
        if (!current(own)) return
        const reason = error instanceof Error && (/^whep_\d{3}$/.test(error.message) || error.message === 'diagnostic_source_unavailable' || error.message === 'diagnostic_tcp_unavailable') ? error.message : 'connection_retry'
        reconnect(reason)
      }
    }

    async function sample(own: Attempt) {
      if (!own.peer || own.sampling || own.statsUnavailable || !current(own)) return
      own.sampling = true
      try {
        const report = await bounded(own.peer.getStats(), 1200, own.abort.signal)
        if (!current(own)) return
        const now: Sample = {
          at: performance.now(), frames: 0, keyFrames: 0, bytes: 0, lost: 0, received: 0,
          decode: 0, buffer: 0, emitted: 0, freezes: 0, jitter: null, rtt: null,
          freezeDuration: null, framesDropped: null, packetsDiscarded: null, localProtocol: null, remoteProtocol: null,
        }
        const addAvailable = (total: number | null, value: unknown) =>
          typeof value === 'number' && Number.isFinite(value) ? (total ?? 0) + value : total
        const maxAvailable = (highest: number | null, value: unknown) =>
          typeof value === 'number' && Number.isFinite(value) ? Math.max(highest ?? 0, value) : highest
        const mediaProtocol = (value: unknown): MediaProtocol | null => value === 'udp' || value === 'tcp' ? value : null
        let found = false
        report.forEach(stat => {
          if (stat.type !== 'inbound-rtp' || (stat.kind ?? stat.mediaType) !== 'video') return
          const mime = String(report.get(stat.codecId)?.mimeType ?? '').toLowerCase()
          const codec = mime.split('/')[1]
          if (['h264', 'h265', 'vp8', 'vp9', 'av1'].includes(codec)) own.codec = codec === 'h265' ? 'hevc' : codec
          found = true
          now.frames += stat.framesDecoded ?? 0
          now.keyFrames += stat.keyFramesDecoded ?? 0
          now.bytes += stat.bytesReceived ?? 0
          now.lost += stat.packetsLost ?? 0
          now.received += stat.packetsReceived ?? 0
          now.decode += stat.totalDecodeTime ?? 0
          now.buffer += stat.jitterBufferDelay ?? 0
          now.emitted += stat.jitterBufferEmittedCount ?? 0
          now.freezes += stat.freezeCount ?? 0
          now.jitter = maxAvailable(now.jitter, stat.jitter)
          now.freezeDuration = addAvailable(now.freezeDuration, stat.totalFreezesDuration)
          now.framesDropped = addAvailable(now.framesDropped, stat.framesDropped)
          now.packetsDiscarded = addAvailable(now.packetsDiscarded, stat.packetsDiscarded)
          const transport = stat.transportId ? report.get(stat.transportId) : undefined
          const pair = transport?.selectedCandidatePairId ? report.get(transport.selectedCandidatePairId) : undefined
          now.rtt = maxAvailable(now.rtt, pair?.currentRoundTripTime)
          const local = pair?.localCandidateId ? report.get(pair.localCandidateId) : undefined
          const remote = pair?.remoteCandidateId ? report.get(pair.remoteCandidateId) : undefined
          now.localProtocol = mediaProtocol(local?.protocol)
          now.remoteProtocol = mediaProtocol(remote?.protocol)
        })
        if (!found) return
        const previous = own.sample
        own.sample = now
        if (now.bytes > (previous?.bytes ?? 0)) own.packetAt = now.at
        if (now.frames > (previous?.frames ?? 0)) {
          own.decodedAt = now.at
          if (!supportsFrameCallback || !onScreen()) frameArrived(own, false)
        }
        if (previous && surfaceRef.current) {
          const seconds = Math.max(0.001, (now.at - previous.at) / 1000)
          const frames = Math.max(0, now.frames - previous.frames), emitted = Math.max(0, now.emitted - previous.emitted)
          const packets = Math.max(0, now.received - previous.received), lost = Math.max(0, now.lost - previous.lost)
          const delta = (value: number | null, before: number | null, scale = 1) =>
            value === null || before === null ? null : Math.round(Math.max(0, value - before) * scale)
          own.metrics = {
            source: own.source,
            protocol: now.localProtocol === now.remoteProtocol ? now.localProtocol : null,
            localProtocol: now.localProtocol,
            remoteProtocol: now.remoteProtocol,
            fps: Math.round(frames / seconds * 10) / 10,
            kbps: Math.round(Math.max(0, now.bytes - previous.bytes) * 8 / seconds / 1000),
            lossPercent: Math.round(lost / Math.max(1, packets + lost) * 1000) / 10,
            decodeMs: frames > 0 ? Math.round((now.decode - previous.decode) * 1000 / frames) : null,
            bufferMs: emitted > 0 ? Math.round((now.buffer - previous.buffer) * 1000 / emitted) : null,
            freezes: now.freezes,
            deltaFreezes: Math.max(0, now.freezes - previous.freezes),
            frozenMs: delta(now.freezeDuration, previous.freezeDuration, 1000),
            jitterMs: now.jitter === null ? null : Math.round(now.jitter * 1000),
            rttMs: now.rtt === null ? null : Math.round(now.rtt * 1000),
            framesDropped: delta(now.framesDropped, previous.framesDropped),
            packetsDiscarded: delta(now.packetsDiscarded, previous.packetsDiscarded),
            sampleMs: Math.round(seconds * 1000),
            frozenMsTotal: now.freezeDuration === null ? null : Math.round(now.freezeDuration * 1000),
            framesDroppedTotal: now.framesDropped,
            packetsDiscardedTotal: now.packetsDiscarded,
          }
          surfaceRef.current.dataset.liveMetrics = JSON.stringify(own.metrics)
          surfaceRef.current.dataset.streamTransport = own.metrics.protocol ?? 'unknown'
        }
        recordDiagnostic(own)
      } catch {
        // Do not accumulate unresolved native promises. Rendered-frame checks
        // remain active; a fresh peer gets its own stats sampling attempt.
        if (current(own)) own.statsUnavailable = true
      }
      finally { own.sampling = false }
    }

    function watchdog() {
      const own = attempt
      if (!own || !current(own) || offline) return
      const now = performance.now()
      void sample(own)
      recordDiagnostic(own)
      if (own.disconnectedAt && now - own.disconnectedAt >= disconnectedGraceMs) {
        reconnect('transport_disconnected')
        return
      }
      if (!own.answeredAt) {
        if (now - own.startedAt > 18000) reconnect('negotiation_timeout')
        return
      }
      // Hidden/occluded videos may stop compositing without losing media.
      if (!onScreen() || now < resumeUntil) return
      const lastFrame = supportsFrameCallback ? own.presentedAt : Math.max(own.decodedAt, own.presentedAt)
      const receiving = own.packetAt > 0 && now - own.packetAt < packetFreshMs
      if (!own.firstFrameAt) {
        const waitingFor = now - own.answeredAt
        if (waitingFor >= firstFrameMs && receiving && waitingFor < keyFrameWaitMs) {
          if (own.phase !== 'awaiting_keyframe') phase(own, 'awaiting_keyframe')
        } else if (waitingFor >= firstFrameMs) reconnect('first_frame_timeout')
        return
      }
      const silentFor = now - (lastFrame || own.firstFrameAt)
      if (silentFor >= 2000 && !own.playbackRetriedAt) {
        own.playbackRetriedAt = now
        void video!.play().catch(() => undefined)
      }
      // Keep a receiving decoder alive long enough for the next IDR after packet
      // loss. No-RTP failures and a frozen compositor retain the fast recovery.
      const decoderWaiting = receiving && own.decodedAt > 0 && now - own.decodedAt >= stalledFrameMs
      if (silentFor >= stalledFrameMs && decoderWaiting && silentFor < keyFrameWaitMs) {
        playing = false
        setState('waiting')
        if (own.phase !== 'awaiting_keyframe') phase(own, 'awaiting_keyframe')
      } else if (silentFor >= stalledFrameMs) reconnect('frame_stalled')
      else if (silentFor < 2000) own.playbackRetriedAt = 0
    }

    function resume() {
      if (disposed || document.hidden) return
      resumeUntil = performance.now() + 2000
      if (offline && navigator.onLine) offline = false
      if (offline) return
      if (!attempt) {
        window.clearTimeout(retryTimer)
        retryTimer = undefined
        reconnect('resuming', true)
      } else {
        void video!.play().catch(() => undefined)
        void sample(attempt)
      }
    }

    function wentOffline() {
      if (disposed) return
      offline = true
      window.clearTimeout(retryTimer)
      retryTimer = undefined
      reconnect('network_offline')
    }

    function wentOnline() {
      if (!offline && attempt) return
      offline = false
      resume()
    }

    function mediaError() { if (attempt) reconnect('video_error') }
    function mediaProgress() {
      reportDimensions()
      // Older browsers fall back to media time and decoder statistics.
      if (!supportsFrameCallback && attempt && video!.currentTime > lastMediaTime && video!.readyState >= 2) {
        lastMediaTime = video!.currentTime
        frameArrived(attempt, true)
      }
    }

    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      const visible = entries.some(entry => entry.isIntersecting)
      if (visible && !inViewport) resumeUntil = performance.now() + 2000
      inViewport = visible
    }) : undefined
    observer?.observe(video)
    video.addEventListener('loadedmetadata', reportDimensions)
    video.addEventListener('playing', mediaProgress)
    video.addEventListener('timeupdate', mediaProgress)
    video.addEventListener('error', mediaError)
    window.addEventListener('offline', wentOffline)
    window.addEventListener('online', wentOnline)
    window.addEventListener('pageshow', resume)
    document.addEventListener('visibilitychange', resume)
    const watchTimer = window.setInterval(watchdog, 1000)
    if (offline) setState('offline')
    else void connect()

    return () => {
      disposed = true
      window.clearInterval(watchTimer)
      window.clearTimeout(retryTimer)
      observer?.disconnect()
      window.removeEventListener('offline', wentOffline)
      window.removeEventListener('online', wentOnline)
      window.removeEventListener('pageshow', resume)
      document.removeEventListener('visibilitychange', resume)
      video.removeEventListener('loadedmetadata', reportDimensions)
      video.removeEventListener('playing', mediaProgress)
      video.removeEventListener('timeupdate', mediaProgress)
      video.removeEventListener('error', mediaError)
      closeAttempt()
    }
  }, [active, getAccess, captureFrame, diagnosticsEnabled, forcedSource, forceTcp, audioEnabled, preferenceScope])

  const recovering = state === 'waiting' || state === 'offline'
  const message = state === 'offline' ? 'Sem conexão. Aguardando a rede…'
    : diagnostic === 'awaiting_keyframe' ? 'Recebendo vídeo. Aguardando imagem…'
    : state === 'waiting' ? 'Reconectando à câmera…'
      : state === 'stopped' ? 'Câmera desativada' : 'Conectando à câmera…'

  return (
    <div ref={surfaceRef} className="stream-player" data-stream-state={state} data-stream-diagnostic={diagnostic} data-stream-source={streamSource}
      data-audio-control-visible={audioControlVisible} onPointerEnter={showAudioControl} onPointerMove={showAudioControl} onPointerDown={showAudioControl}>
      <video ref={videoRef} autoPlay muted={!audioEnabled} playsInline aria-label={'Transmissão ao vivo: ' + name} />
      {active && companyId && cameraId && <CustomLivePlayer companyId={companyId} cameraId={cameraId} name={name} speedOnly />}
      <button type="button" className="stream-audio-toggle" aria-label={audioEnabled ? 'Desativar áudio' : 'Ativar áudio'} title={audioEnabled ? 'Desativar áudio' : 'Ativar áudio'} aria-pressed={audioEnabled} disabled={!active}
        onClick={event => {
          event.stopPropagation()
          showAudioControl()
          if (videoRef.current) {
            videoRef.current.muted = audioEnabled
            // Keep playback permission tied to the user's gesture on mobile browsers.
            if (!audioEnabled) void videoRef.current.play().catch(() => undefined)
          }
          setAudioChoice({ name, enabled: !audioEnabled })
        }}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M11 5 6 9H3v6h3l5 4z" />
          {audioEnabled ? <path d="M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14" /> : <path d="m16 9 5 6m0-6-5 6" />}
        </svg>
      </button>
      {fallbackFrame && state !== 'playing' && <img className="stream-last-frame" src={fallbackFrame} alt="" aria-hidden="true" />}
      {hasRenderedVideo && recovering ? (
        <div className="stream-recovery" role="status"><span className="camera-pulse" />{message}</div>
      ) : state !== 'playing' && (
        <div className="stream-state" role="status">
          <span className={state === 'connecting' ? 'spinner' : 'camera-pulse'} />{message}
        </div>
      )}
      {diagnosticsEnabled && <details style={{ position: 'absolute', top: 8, right: 8, zIndex: 4, maxWidth: '95%', maxHeight: '80%', overflow: 'auto', padding: 8, color: '#fff', background: '#071b2ef2', fontSize: 11 }} onClick={event => event.stopPropagation()}>
        <summary>Diagnóstico de vídeo</summary>
        <button type="button" onClick={downloadDiagnostics}>Baixar diagnóstico JSON</button>
        <pre aria-label={`Diagnóstico de ${name}`} style={{ whiteSpace: 'pre-wrap', maxWidth: 350 }}>{JSON.stringify(diagnosticSnapshot, null, 2)}</pre>
      </details>}
    </div>
  )
}
